import { ReactNode, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { keyBy, mergeWith } from 'lodash';
import { Flex } from '@chakra-ui/react';
import Table from './tableComponents/Table';
import { TInput, TTab } from '../tabs/type';
import { isEmpty, strNomalize } from '../../helpers';
import { addressInputs, addressInputskeys } from './actions/ManageInputs';
import useTableContainer from './TableContainer';
import { TState } from '../../redux/store';
import { useCanI } from '../../redux/actions';
import useDynamicFiltersContainer from './tableComponents/DynamicFiltersContainer';
import DynamicFilters from './tableComponents/DynamicFilters';
import KPIs from '../stats/KPI';
import { fetchComments } from '../../api/tabsApi';

export type TColumn = {
  header?: ReactNode;
  accessorKey: string;
  accessorFn?: (row: any, idx: number) => any;
  meta: {
    _id: string;
  } & Record<string, any>;
};

const formatForTable = (
  selectById: Record<string, TInput>,
  userList: Record<string, any>[],
  canI: any,
  columns: any[] = [],
  user: any,
  getComm?: any,
) => {
  return columns?.reduce((prev: any, column: any) => {
    if (!column.name_id) return prev;

    const correctInput = selectById[column.baseName || column.name_id];

    const metas = {
      _id: column.name_id || '',
      type: column.type,
      unity: column.unity,
      dated: column.dated,
      isAgo: column.isAgo,
      showTime: correctInput?.showTime ?? column.showTime,
      nbRepeat: column.nbRepeat,
    };

    let col: any = [
      {
        id: column.name_id,
        accessorKey: column.label,
        accessorFn: (row: any) => row[column.name_id || ''],
        meta: metas,
      },
    ];

    if (
      column.name_id &&
      column.type !== 'address' &&
      !canI(column.name_id, 'read') &&
      !canI('comments', 'read')
    ) {
      return prev;
    }

    if (column.type === 'comm') {
      col = [
        {
          id: column.name_id,
          header: column.label,
          accessorFn: (row: any) => {
            getComm?.(row._id);
          },
          meta: { ...metas, comms: true },
        },
      ];
    }

    if (/select/.test(column.type) && correctInput) {
      col = [
        {
          id: column.name_id,
          accessorKey: column.label,
          accessorFn: (row: any) =>
            (!isEmpty(row[column.name_id]) &&
              (correctInput?.type === 'multiselect'
                ? row[column.name_id]
                    .map((s: any) => correctInput?.options?.[Number(s)])
                    .join(', ')
                : correctInput?.options?.[Number(row[column.name_id])])) ||
            '-',
          meta: {
            ...metas,
            colors: correctInput?.colors,
            colorKey: column.name_id,
            options: correctInput?.options,
            isMulti: correctInput?.type === 'multiselect',
          },
        },
      ];
    }

    if (column.type === 'users') {
      const usersById = keyBy(userList, '_id');
      const name_id = `${column?.name_id}`;
      const colors = userList.reduce(
        (prev, user) => ({
          ...prev,
          [user._id]: user.userColor,
        }),
        {},
      );

      col = [
        {
          id: column.name_id,
          accessorKey: column.label,
          accessorFn: (row: any) => {
            return (
              usersById[row[name_id]]?.name ||
              usersById[row[name_id]]?.login ||
              '-'
            );
          },
          meta: {
            ...metas,
            colors: colors,
            colorKey: name_id,
          },
        },
      ];
    }

    if (column.type === 'address') {
      col = addressInputskeys.reduce(
        (prevField, inputKey, idx) =>
          !(column as any)[inputKey] ||
          !canI(`${column.name_id}_${inputKey}`, 'read')
            ? prevField
            : [
                ...prevField,
                {
                  id: `${column.name_id}_${inputKey}`,
                  accessorKey: `${addressInputs[idx]} ${column.label}`,
                  accessorFn: (row: any) =>
                    row[`${column.name_id}_${inputKey}`],
                  meta: {
                    ...metas,
                    _id: strNomalize(`${column.name_id}_${addressInputs[idx]}`),
                    name: `${addressInputs[idx]} ${column.label}`,
                  },
                },
              ],
        [] as any,
      );

      if (column.showValidation) {
        col.push({
          id: column.name_id,
          accessorKey: `Valide`,
          accessorFn: (row: any) => (row[`${column.name_id}`] ? 'oui' : 'non'),
          meta: {
            ...metas,
            _id: `vaidation_${column.label}`,
            showValidation: true,
          },
        });
      }
    }

    if (column.type === 'repeatgroup') {
      col = Array.from({ length: column.nbRepeatFieldsTable || 0 }).flatMap(
        (_, idx) =>
          (column.repeatFieldsTable || column.repeatFields)?.reduce(
            (prev: any, name_id: string) => [
              ...prev,
              ...formatForTable(
                selectById,
                userList,
                canI,
                [
                  {
                    ...selectById[name_id],
                    name_id: `repeat_${name_id}_${idx}`,
                    baseName: name_id,
                    label: `${selectById[name_id].label || ''} ${idx + 1}`,
                    nbRepeat: column.nbRepeatFieldsTable,
                  },
                ],
                user,
              ),
            ],
            [],
          ),
      );

      col = [
        ...col,
        ...((column.repeatFieldsTable || column.repeatFields)?.map(
          (key: string) => ({
            id: key,
            meta: {
              ...metas,
              hide: true,
              nbRepeat: column.nbRepeatFieldsTable || 0,
            },
          }),
        ) || []),
      ];
    }

    const hasAccess = user.role_id.some((role: string) =>
      column.datedRoles?.includes(role),
    );

    const displayDatedCol =
      user.isAdmin || user.isSuper || !column?.datedRoles?.length || hasAccess;

    if (column.dated && displayDatedCol) {
      col.push({
        header: 'Modifié le',
        id: `${column.name_id}-dated`,
        accessorFn: (row: any) => row[`${column.name_id}-dated`],
        meta: {
          ...metas,
          _id: `${column.name_id}-dated`,
          type: 'date',
          datedCol: true,
        },
      });
    }

    return [...prev, ...col];
  }, [] as any);
};

const DynamicTable = ({ tab }: { tab?: TTab }) => {
  const data = useSelector((state: TState) => state.data);
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const userList = useSelector((state: TState) => state.userList);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const user = useSelector((state: TState) => state.user);

  const canI = useCanI();

  const currTab = tab || currentTab || ({} as TTab);
  const tableColumns = currTab.tableColumns?.[0]?.tableColumns || [];
  const commsById = useRef({});

  const columns = useMemo(
    () =>
      formatForTable(
        keyBy(dataInputs, 'name_id'),
        userList,
        canI,
        tableColumns,
        user,
        async (id: string) => {
          const comms = await fetchComments(id, true);
          commsById.current = mergeWith(commsById.current, {
            [id]: comms.data,
          });
        },
      ),
    [tab, currentTab, dataInputs, userList],
  );

  const editEntryKey = useMemo(
    () => (currentTab?.isDuplicate ? 'editDuplicate' : 'editEntry'),
    [currentTab?._id],
  );

  const { table, ...props } = useTableContainer({
    data,
    columns,
    editEntryKey,
    pendingKey: 'global/async/getData/pending',
    noActions: true,
  });

  const dynamicFiltersProps = useDynamicFiltersContainer(table);

  return (tab || currentTab || {})?._id ? (
    <Flex direction='column' gap={2} overflow='hidden' height='100%'>
      {!props.isPending && (
        <KPIs tableInstance={table} rows={props.rows} currTab={currTab} />
      )}
      <DynamicFilters {...dynamicFiltersProps} />
      <Table {...props} table={null as any} commsById={commsById.current} />
    </Flex>
  ) : null;
};

export default DynamicTable;
